import React from "react";
import { Box, InputBase, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const SearchIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

// Vertical divider between filter groups
const Divider = ({ t }) => (
  <Box sx={{
    width: "1px", height: "16px",
    background: t.border2, flexShrink: 0, mx: "2px",
  }} />
);

// Single filter group — label + toggle chips
const FilterChipGroup = ({ label, options, active, onChange, t }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
    <Typography sx={{
      fontFamily: "'DM Mono', monospace",
      fontSize: "9px", letterSpacing: "0.1em",
      textTransform: "uppercase", color: t.text3,
      mr: "2px",
    }}>
      {label}
    </Typography>
    {options.map((opt) => {
      const isActive = active === opt.value;
      return (
        <Box
          key={opt.value}
          onClick={() => onChange(opt.value)}
          sx={{
            padding: "3px 9px", borderRadius: "3px", cursor: "pointer",
            fontFamily: "'DM Mono', monospace",
            fontSize: "9px", letterSpacing: "0.06em",
            fontWeight: isActive ? 600 : 400,
            border:     isActive ? `1px solid ${t.accent}88` : `1px solid ${t.border2}`,
            background: isActive ? t.accent2 : "transparent",
            color:      isActive ? t.accent  : t.text3,
            transition: "all 0.15s",
            "&:hover":  { borderColor: t.accent + "66", color: t.accent, background: t.accent2 },
          }}
        >
          {opt.label}
        </Box>
      );
    })}
  </Box>
);

const FilterBar = ({ filters, onFilterChange, onClearAll, search, onSearchChange }) => {
  const theme = useTheme();
  const t = theme.custom;

  const hasActiveFilter = Object.values(filters).some(Boolean);

  return (
    <Box sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "10px 20px", borderBottom: `1px solid ${t.border}`,
      gap: "10px", flexWrap: "wrap",
    }}>

      {/* Filter chip groups */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>

        <FilterChipGroup
          label="Type"
          options={[
            { value: "BIL", label: "BIL" },
            { value: "UBL", label: "UBL" },
          ]}
          active={filters.type}
          onChange={(v) => onFilterChange("type", v)}
          t={t}
        />

        <Divider t={t} />

        <FilterChipGroup
          label="Status"
          options={[
            { value: "ACTIVE", label: "Active" },
            { value: "OPEN",   label: "Open"   },
            { value: "ENDED",  label: "Ended"  },
          ]}
          active={filters.status}
          onChange={(v) => onFilterChange("status", v)}
          t={t}
        />

      

        {/* Clear all — only visible when at least one filter is active */}
        {hasActiveFilter && (
          <Box
            onClick={onClearAll}
            sx={{
              padding: "3px 10px", borderRadius: "3px", cursor: "pointer",
              fontFamily: "'DM Mono', monospace", fontSize: "9px",
              color: "#c84040", border: "1px solid rgba(200,64,64,0.25)",
              background: "rgba(200,64,64,0.08)", letterSpacing: "0.06em",
              transition: "all 0.15s",
              "&:hover": { background: "rgba(200,64,64,0.18)" },
            }}
          >
            Clear
          </Box>
        )}
      </Box>

      {/* Search box */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: "8px",
        background: t.surface2, border: `1px solid ${t.border2}`,
        borderRadius: "4px", padding: "6px 12px", width: "260px",
      }}>
        <Box sx={{ color: t.text3 }}><SearchIcon /></Box>
        <InputBase
          placeholder="Search ID, role, SOW, associate..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ flex: 1, fontFamily: "'Inter', sans-serif", fontSize: "12px", color: t.text }}
        />
      </Box>
    </Box>
  );
};

export default FilterBar;